import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { DollarSign, TrendingUp, Users, AlertCircle, Plus, Edit, Trash2, Download, Calendar } from 'lucide-react';
import { format, parseISO, subDays, addDays, startOfDay, isWithinInterval } from 'date-fns';
import { toast } from 'sonner';
import { logAdminAction } from '@/lib/auditLog';
import { rowsToCsv, downloadCsv } from '@/lib/csvExport';

const STATUS_COLORS: Record<string, string> = {
  active: 'hsl(var(--primary))',
  pending: 'hsl(var(--accent))',
  expired: 'hsl(var(--muted-foreground))',
  cancelled: 'hsl(var(--destructive))',
};

export function RevenueDashboard() {
  const qc = useQueryClient();
  const [windowDays, setWindowDays] = useState(30);

  // Date range filters (default = last `windowDays`)
  const [rangeStart, setRangeStart] = useState<string>(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [rangeEnd, setRangeEnd] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  // Subscription dialog
  const [subOpen, setSubOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<any | null>(null);
  const [subUserId, setSubUserId] = useState('');
  const [subPackageId, setSubPackageId] = useState('');
  const [subAmount, setSubAmount] = useState('');
  const [subPaymentRef, setSubPaymentRef] = useState('');
  const [subDuration, setSubDuration] = useState(30);

  // Package dialog
  const [pkgOpen, setPkgOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState<any | null>(null);
  const [pkgName, setPkgName] = useState('');
  const [pkgAudience, setPkgAudience] = useState('individual');
  const [pkgPrice, setPkgPrice] = useState('');
  const [pkgDuration, setPkgDuration] = useState(30);
  const [pkgFeatures, setPkgFeatures] = useState('');
  const [pkgActive, setPkgActive] = useState(true);
  const [pkgPopular, setPkgPopular] = useState(false);
  const [pkgSort, setPkgSort] = useState(0);

  const { data: packages, isLoading: loadingPkgs } = useQuery({
    queryKey: ['adv-packages-admin'],
    queryFn: async () => {
      const { data } = await supabase.from('advertising_packages' as any).select('*').order('sort_order');
      return data || [];
    },
  });

  const { data: subscriptions, isLoading: loadingSubs } = useQuery({
    queryKey: ['subscriptions-admin'],
    queryFn: async () => {
      const { data } = await supabase.from('subscriptions' as any).select('*').order('created_at', { ascending: false });
      return data || [];
    },
  });

  const { data: invoices, isLoading: loadingInv } = useQuery({
    queryKey: ['invoices-admin'],
    queryFn: async () => {
      const { data } = await supabase.from('invoices' as any).select('*').order('issued_at', { ascending: false });
      return data || [];
    },
  });

  // KPIs & charts
  const kpis = useMemo(() => {
    const subs = (subscriptions ?? []) as any[];
    const now = new Date();
    const activeSubs = subs.filter(s => s.status === 'active' && parseISO(s.expires_at) > now);
    const mrr = activeSubs.reduce((sum, s) => sum + Number(s.amount_etb || 0), 0);
    const since = subDays(now, windowDays);
    const revInWindow = subs
      .filter(s => parseISO(s.created_at) >= since)
      .reduce((sum, s) => sum + Number(s.amount_etb || 0), 0);
    const expiringSoon = subs.filter(s => {
      const exp = parseISO(s.expires_at);
      return s.status === 'active' && exp > now && exp <= addDays(now, 7);
    }).length;
    return { mrr, revInWindow, activeCount: activeSubs.length, expiringSoon };
  }, [subscriptions, windowDays]);

  const revenueByDay = useMemo(() => {
    const subs = (subscriptions ?? []) as any[];
    const map = new Map<string, number>();
    for (let i = windowDays - 1; i >= 0; i--) {
      const d = format(subDays(new Date(), i), 'MMM dd');
      map.set(d, 0);
    }
    subs.forEach(s => {
      const d = format(parseISO(s.created_at), 'MMM dd');
      if (map.has(d)) map.set(d, (map.get(d) || 0) + Number(s.amount_etb || 0));
    });
    return Array.from(map.entries()).map(([date, revenue]) => ({ date, revenue }));
  }, [subscriptions, windowDays]);

  const revenueByPackage = useMemo(() => {
    const subs = (subscriptions ?? []) as any[];
    const pkgMap = new Map((packages ?? []).map((p: any) => [p.id, p.name]));
    const m = new Map<string, number>();
    subs.forEach(s => {
      const name = pkgMap.get(s.package_id) || 'Other';
      m.set(name, (m.get(name) || 0) + Number(s.amount_etb || 0));
    });
    return Array.from(m.entries()).map(([name, value]) => ({ name, value }));
  }, [subscriptions, packages]);

  const statusMix = useMemo(() => {
    const subs = (subscriptions ?? []) as any[];
    const m = new Map<string, number>();
    subs.forEach(s => m.set(s.status, (m.get(s.status) || 0) + 1));
    return Array.from(m.entries()).map(([name, value]) => ({ name, value }));
  }, [subscriptions]);

  // Range-filtered datasets for tables + exports
  const inRange = (iso?: string | null) => {
    if (!iso) return false;
    try {
      const d = parseISO(iso);
      return isWithinInterval(d, { start: parseISO(rangeStart), end: parseISO(rangeEnd + 'T23:59:59') });
    } catch { return false; }
  };

  const filteredSubs = useMemo(
    () => ((subscriptions ?? []) as any[]).filter(s => inRange(s.created_at)),
    [subscriptions, rangeStart, rangeEnd]
  );
  const filteredInv = useMemo(
    () => ((invoices ?? []) as any[]).filter(i => inRange(i.issued_at)),
    [invoices, rangeStart, rangeEnd]
  );
  const refundedInv = useMemo(
    () => filteredInv.filter(i => i.status === 'refunded'),
    [filteredInv]
  );

  const exportSubs = () => {
    const rows = filteredSubs.map(s => {
      const pkg: any = (packages ?? []).find((p: any) => p.id === s.package_id);
      return {
        id: s.id, user_id: s.user_id, package: pkg?.name ?? '',
        status: s.status, amount_etb: s.amount_etb,
        started_at: s.started_at, expires_at: s.expires_at,
        payment_ref: s.payment_ref ?? '', created_at: s.created_at,
      };
    });
    if (!rows.length) { toast.error('No subscriptions in range'); return; }
    downloadCsv(`subscriptions_${rangeStart}_to_${rangeEnd}`, rowsToCsv(rows));
  };
  const exportInvoices = (onlyRefunded = false) => {
    const src = onlyRefunded ? refundedInv : filteredInv;
    const rows = src.map(i => ({
      invoice_number: i.invoice_number ?? i.id, user_id: i.user_id,
      amount_etb: i.amount_etb, status: i.status,
      issued_at: i.issued_at, paid_at: i.paid_at ?? '', notes: i.notes ?? '',
    }));
    if (!rows.length) { toast.error('No invoices in range'); return; }
    const label = onlyRefunded ? 'refunds' : 'invoices';
    downloadCsv(`${label}_${rangeStart}_to_${rangeEnd}`, rowsToCsv(rows));
  };
  const exportMrrTrend = () => {
    if (!revenueByDay.length) { toast.error('No trend data'); return; }
    downloadCsv(`mrr_trend_${windowDays}d`, rowsToCsv(revenueByDay));
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['subscriptions-admin'] });
    qc.invalidateQueries({ queryKey: ['invoices-admin'] });
    qc.invalidateQueries({ queryKey: ['adv-packages-admin'] });
  };

  // ── Subscription actions ──
  const openNewSub = () => {
    setEditingSub(null); setSubUserId(''); setSubPackageId(''); setSubAmount(''); setSubPaymentRef(''); setSubDuration(30);
    setSubOpen(true);
  };
  const saveSubscription = async () => {
    if (!subUserId || !subPackageId) { toast.error('User ID and package required'); return; }
    const pkg: any = (packages ?? []).find((p: any) => p.id === subPackageId);
    const expiresAt = addDays(new Date(), subDuration).toISOString();
    const payload = {
      user_id: subUserId,
      package_id: subPackageId,
      amount_etb: parseFloat(subAmount) || Number(pkg?.price_etb || 0),
      payment_ref: subPaymentRef || null,
      status: 'active',
      expires_at: expiresAt,
    };
    if (editingSub) {
      const { error } = await supabase.from('subscriptions' as any).update(payload).eq('id', editingSub.id);
      if (error) { toast.error(error.message); return; }
      await logAdminAction({ action: 'update', entity_type: 'subscription', entity_id: editingSub.id, metadata: payload });
    } else {
      const { data, error } = await supabase.from('subscriptions' as any).insert(payload).select('id').single();
      if (error) { toast.error(error.message); return; }
      const newSub: any = data;
      // Auto-create matching invoice
      await supabase.from('invoices' as any).insert({
        subscription_id: newSub?.id,
        user_id: subUserId,
        amount_etb: payload.amount_etb,
        invoice_number: `INV-${Date.now().toString(36).toUpperCase()}`,
        status: payload.payment_ref ? 'paid' : 'unpaid',
        paid_at: payload.payment_ref ? new Date().toISOString() : null,
      });
      await logAdminAction({ action: 'create', entity_type: 'subscription', entity_id: newSub?.id, metadata: payload });
    }
    toast.success('Subscription saved');
    setSubOpen(false); refresh();
  };

  const extendSub = async (s: any, days = 30) => {
    const newExp = addDays(parseISO(s.expires_at), days).toISOString();
    const { error } = await supabase.from('subscriptions' as any).update({ expires_at: newExp, status: 'active' }).eq('id', s.id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: 'extend', entity_type: 'subscription', entity_id: s.id, metadata: { days, new_expiry: newExp } });
    toast.success(`Extended ${days} days`); refresh();
  };

  const cancelSub = async (s: any) => {
    if (!confirm('Cancel this subscription?')) return;
    const { error } = await supabase.from('subscriptions' as any).update({ status: 'cancelled' }).eq('id', s.id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: 'cancel', entity_type: 'subscription', entity_id: s.id });
    toast.success('Cancelled'); refresh();
  };

  // ── Invoice actions ──
  const setInvoiceStatus = async (inv: any, status: 'paid' | 'unpaid' | 'refunded') => {
    const update: any = { status };
    if (status === 'paid') update.paid_at = new Date().toISOString();
    if (status === 'refunded') update.notes = (inv.notes || '') + `\nRefunded ${new Date().toISOString()}`;
    const { error } = await supabase.from('invoices' as any).update(update).eq('id', inv.id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: `mark_${status}`, entity_type: 'invoice', entity_id: inv.id });
    toast.success('Updated'); refresh();
  };

  // ── Package actions ──
  const openNewPkg = () => {
    setEditingPkg(null);
    setPkgName(''); setPkgAudience('individual'); setPkgPrice(''); setPkgDuration(30);
    setPkgFeatures(''); setPkgActive(true); setPkgPopular(false); setPkgSort(0);
    setPkgOpen(true);
  };
  const openEditPkg = (p: any) => {
    setEditingPkg(p);
    setPkgName(p.name); setPkgAudience(p.audience); setPkgPrice(String(p.price_etb)); setPkgDuration(p.duration_days);
    setPkgFeatures((p.features || []).join('\n'));
    setPkgActive(p.is_active); setPkgPopular(!!p.is_popular); setPkgSort(p.sort_order || 0);
    setPkgOpen(true);
  };
  const savePkg = async () => {
    const features = pkgFeatures.split('\n').map(s => s.trim()).filter(Boolean);
    const payload: any = {
      name: pkgName, audience: pkgAudience, price_etb: parseFloat(pkgPrice) || 0,
      duration_days: pkgDuration, features, is_active: pkgActive, is_popular: pkgPopular, sort_order: pkgSort,
    };
    if (editingPkg) {
      const { error } = await supabase.from('advertising_packages' as any).update(payload).eq('id', editingPkg.id);
      if (error) { toast.error(error.message); return; }
      await logAdminAction({ action: 'update', entity_type: 'advertising_package', entity_id: editingPkg.id, metadata: payload });
    } else {
      const { data, error } = await supabase.from('advertising_packages' as any).insert(payload).select('id').single();
      if (error) { toast.error(error.message); return; }
      await logAdminAction({ action: 'create', entity_type: 'advertising_package', entity_id: (data as any)?.id, metadata: payload });
    }
    toast.success('Package saved'); setPkgOpen(false); refresh();
  };
  const togglePkgActive = async (p: any) => {
    const { error } = await supabase.from('advertising_packages' as any).update({ is_active: !p.is_active }).eq('id', p.id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: p.is_active ? 'deactivate' : 'activate', entity_type: 'advertising_package', entity_id: p.id });
    refresh();
  };
  const deletePkg = async (p: any) => {
    if (!confirm(`Delete package "${p.name}"?`)) return;
    const { error } = await supabase.from('advertising_packages' as any).delete().eq('id', p.id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: 'delete', entity_type: 'advertising_package', entity_id: p.id });
    toast.success('Deleted'); refresh();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><DollarSign className="h-6 w-6 text-primary" /> Revenue & Subscriptions</h1>
          <p className="text-sm text-muted-foreground">Track packages sold, MRR, invoices, and renewals.</p>
        </div>
        <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(parseInt(v))}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last 365 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Date range filter bar — applies to subscriptions, invoices, refunds */}
      <Card>
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <Label className="text-xs">From</Label>
          <Input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} className="w-[160px] h-8" />
          <Label className="text-xs">To</Label>
          <Input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} className="w-[160px] h-8" />
          <span className="text-xs text-muted-foreground">
            {filteredSubs.length} subs · {filteredInv.length} invoices · {refundedInv.length} refunds in range
          </span>
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="outline" onClick={exportMrrTrend}><Download className="h-3.5 w-3.5 mr-1" /> MRR trend</Button>
            <Button size="sm" variant="outline" onClick={exportSubs}><Download className="h-3.5 w-3.5 mr-1" /> Subscriptions</Button>
            <Button size="sm" variant="outline" onClick={() => exportInvoices(false)}><Download className="h-3.5 w-3.5 mr-1" /> Invoices</Button>
            <Button size="sm" variant="outline" onClick={() => exportInvoices(true)}><Download className="h-3.5 w-3.5 mr-1" /> Refunds</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<TrendingUp className="h-5 w-5" />} label="MRR" value={`ETB ${kpis.mrr.toLocaleString()}`} hint="Active subscription value" />
        <KpiCard icon={<DollarSign className="h-5 w-5" />} label={`Revenue (${windowDays}d)`} value={`ETB ${kpis.revInWindow.toLocaleString()}`} />
        <KpiCard icon={<Users className="h-5 w-5" />} label="Active subs" value={String(kpis.activeCount)} />
        <KpiCard icon={<AlertCircle className="h-5 w-5" />} label="Expiring this week" value={String(kpis.expiringSoon)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Revenue over time</CardTitle></CardHeader>
          <CardContent style={{ height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={revenueByDay}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Status mix</CardTitle></CardHeader>
          <CardContent style={{ height: 280 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={statusMix} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {statusMix.map((entry, i) => <Cell key={i} fill={STATUS_COLORS[entry.name] || 'hsl(var(--muted))'} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Revenue by package</CardTitle></CardHeader>
        <CardContent style={{ height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={revenueByPackage}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Tabs defaultValue="subscriptions">
        <TabsList>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="packages">Packages</TabsTrigger>
        </TabsList>

        <TabsContent value="subscriptions">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">All subscriptions</CardTitle>
              <Button size="sm" onClick={openNewSub}><Plus className="h-4 w-4 mr-1" /> New subscription</Button>
            </CardHeader>
            <CardContent>
              {loadingSubs ? <Skeleton className="h-40 w-full" /> : (subscriptions || []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No subscriptions yet.</p>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Package</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSubs.map((s: any) => {
                        const pkg = (packages ?? []).find((p: any) => p.id === s.package_id) as any;
                        return (
                          <TableRow key={s.id}>
                            <TableCell className="text-xs font-mono">{s.user_id.slice(0, 8)}…</TableCell>
                            <TableCell>{pkg?.name || '—'}</TableCell>
                            <TableCell className="text-sm">{format(parseISO(s.started_at), 'MMM d, yyyy')}</TableCell>
                            <TableCell className="text-sm">{format(parseISO(s.expires_at), 'MMM d, yyyy')}</TableCell>
                            <TableCell>ETB {Number(s.amount_etb).toLocaleString()}</TableCell>
                            <TableCell><Badge variant={s.status === 'active' ? 'default' : 'outline'}>{s.status}</Badge></TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" variant="ghost" onClick={() => extendSub(s, 30)}>+30d</Button>
                              <Button size="sm" variant="ghost" onClick={() => cancelSub(s)}>Cancel</Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices">
          <Card>
            <CardHeader><CardTitle className="text-base">All invoices</CardTitle></CardHeader>
            <CardContent>
              {loadingInv ? <Skeleton className="h-40 w-full" /> : (invoices || []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No invoices yet.</p>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Issued</TableHead>
                        <TableHead>Paid</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInv.map((inv: any) => (
                        <TableRow key={inv.id}>
                          <TableCell className="text-xs font-mono">{inv.invoice_number || inv.id.slice(0, 8)}</TableCell>
                          <TableCell className="text-xs font-mono">{inv.user_id.slice(0, 8)}…</TableCell>
                          <TableCell>ETB {Number(inv.amount_etb).toLocaleString()}</TableCell>
                          <TableCell><Badge variant={inv.status === 'paid' ? 'default' : inv.status === 'refunded' ? 'destructive' : 'secondary'}>{inv.status}</Badge></TableCell>
                          <TableCell className="text-sm">{format(parseISO(inv.issued_at), 'MMM d, yyyy')}</TableCell>
                          <TableCell className="text-sm">{inv.paid_at ? format(parseISO(inv.paid_at), 'MMM d, yyyy') : '—'}</TableCell>
                          <TableCell className="text-right">
                            {inv.status !== 'paid' && <Button size="sm" variant="ghost" onClick={() => setInvoiceStatus(inv, 'paid')}>Mark paid</Button>}
                            {inv.status !== 'refunded' && <Button size="sm" variant="ghost" onClick={() => setInvoiceStatus(inv, 'refunded')}>Refund</Button>}
                            {inv.pdf_url && <a href={inv.pdf_url} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost">PDF</Button></a>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="packages">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Advertising packages</CardTitle>
              <Button size="sm" onClick={openNewPkg}><Plus className="h-4 w-4 mr-1" /> New package</Button>
            </CardHeader>
            <CardContent>
              {loadingPkgs ? <Skeleton className="h-40 w-full" /> : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Audience</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Sort</TableHead>
                        <TableHead>Active</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(packages ?? []).map((p: any) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium">{p.name}{p.is_popular && <Badge variant="secondary" className="ml-2 text-[10px]">Popular</Badge>}</TableCell>
                          <TableCell className="capitalize">{p.audience}</TableCell>
                          <TableCell>ETB {Number(p.price_etb).toLocaleString()}</TableCell>
                          <TableCell>{p.duration_days}d</TableCell>
                          <TableCell>{p.sort_order}</TableCell>
                          <TableCell><Switch checked={p.is_active} onCheckedChange={() => togglePkgActive(p)} /></TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => openEditPkg(p)}><Edit className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => deletePkg(p)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Subscription dialog */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingSub ? 'Edit' : 'New'} subscription</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>User ID (UUID)</Label><Input value={subUserId} onChange={e => setSubUserId(e.target.value)} placeholder="xxxxxxxx-xxxx-…" /></div>
            <div>
              <Label>Package</Label>
              <Select value={subPackageId} onValueChange={setSubPackageId}>
                <SelectTrigger><SelectValue placeholder="Choose package" /></SelectTrigger>
                <SelectContent>
                  {(packages ?? []).map((p: any) => <SelectItem key={p.id} value={p.id}>{p.name} — ETB {p.price_etb}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (ETB)</Label><Input type="number" value={subAmount} onChange={e => setSubAmount(e.target.value)} placeholder="auto from package" /></div>
              <div><Label>Duration (days)</Label><Input type="number" value={subDuration} onChange={e => setSubDuration(parseInt(e.target.value) || 30)} /></div>
            </div>
            <div><Label>Payment ref (optional)</Label><Input value={subPaymentRef} onChange={e => setSubPaymentRef(e.target.value)} placeholder="TX-…" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setSubOpen(false)}>Cancel</Button><Button onClick={saveSubscription}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Package dialog */}
      <Dialog open={pkgOpen} onOpenChange={setPkgOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingPkg ? 'Edit' : 'New'} package</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={pkgName} onChange={e => setPkgName(e.target.value)} /></div>
              <div>
                <Label>Audience</Label>
                <Select value={pkgAudience} onValueChange={setPkgAudience}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="agency">Agency</SelectItem>
                    <SelectItem value="developer">Developer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Price (ETB)</Label><Input type="number" value={pkgPrice} onChange={e => setPkgPrice(e.target.value)} /></div>
              <div><Label>Duration (days)</Label><Input type="number" value={pkgDuration} onChange={e => setPkgDuration(parseInt(e.target.value) || 30)} /></div>
              <div><Label>Sort order</Label><Input type="number" value={pkgSort} onChange={e => setPkgSort(parseInt(e.target.value) || 0)} /></div>
              <div className="flex items-end gap-4">
                <div className="flex items-center gap-2"><Switch checked={pkgActive} onCheckedChange={setPkgActive} /><span className="text-sm">Active</span></div>
                <div className="flex items-center gap-2"><Switch checked={pkgPopular} onCheckedChange={setPkgPopular} /><span className="text-sm">Popular</span></div>
              </div>
            </div>
            <div><Label>Features (one per line)</Label><Textarea rows={5} value={pkgFeatures} onChange={e => setPkgFeatures(e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPkgOpen(false)}>Cancel</Button><Button onClick={savePkg}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{label}</p>
          <div className="text-primary">{icon}</div>
        </div>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}
